import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { useMobile, useVibration } from '../hooks/useMobile';
import TouchOptimizedEditor from './TouchOptimizedEditor';
import VoiceCoding from './VoiceCoding';
import AIAssistant from './AIAssistant';
import SettingsModal from './SettingsModal';
import AuthModal from './AuthModal';
import UserMenu from './UserMenu';
import FilePublisher from './FilePublisher';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as BiIcons from 'react-icons/bi';

const { FiMenu, FiX, FiFile, FiPlus, FiEye, FiSettings, FiSave, FiFolder, FiChevronDown, FiMaximize, FiMinimize, FiMic, FiHome, FiUser, FiLogIn, FiCode, FiGlobe, FiExternalLink, FiWifi } = FiIcons;
const { BiBot } = BiIcons;

const MobileCodeEditor = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentProject, files, activeFile, updateFile, createFile, setActiveFile, syncing, saveCurrentProject } = useProject();
  const { settings, updateSettings } = useSettings();
  const { isMobile, orientation } = useMobile();
  const { vibrateTouch } = useVibration();

  // Unified menu state
  const [showUnifiedMenu, setShowUnifiedMenu] = useState(false);
  const [showFileList, setShowFileList] = useState(false);
  const [showNewFileDialog, setShowNewFileDialog] = useState(false);
  const [showFilePublisher, setShowFilePublisher] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [selectedExtension, setSelectedExtension] = useState('html');
  const [showVoiceInput, setShowVoiceInput] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const editorRef = useRef(null);

  // File extensions for new file creation
  const fileExtensions = [
    { ext: 'html', name: 'HTML File' },
    { ext: 'css', name: 'CSS File' },
    { ext: 'js', name: 'JavaScript File' },
    { ext: 'jsx', name: 'React JSX File' },
    { ext: 'ts', name: 'TypeScript File' },
    { ext: 'tsx', name: 'TypeScript React File' },
    { ext: 'json', name: 'JSON File' },
    { ext: 'md', name: 'Markdown File' },
    { ext: 'txt', name: 'Text File' }
  ];

  if (!currentProject) {
    navigate('/');
    return null;
  }

  const handleEditorChange = (value) => {
    if (activeFile) {
      updateFile(activeFile, value || '');
    }
  };

  const getLanguage = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'js':
      case 'jsx':
        return 'javascript';
      case 'ts':
      case 'tsx':
        return 'typescript';
      case 'css':
        return 'css';
      case 'html':
        return 'html';
      case 'json':
        return 'json';
      case 'md':
        return 'markdown';
      default:
        return 'plaintext';
    }
  };

  const handleVoiceCommand = (command) => {
    vibrateTouch();
    switch (command) {
      case 'save':
        handleSave();
        break;
      case 'preview':
        togglePreview();
        break;
      case 'newFile':
        setShowFileList(true);
        break;
      case 'settings':
        setShowSettings(true);
        break;
      default:
        break;
    }
  };

  const handleVoiceInsert = (code) => {
    if (activeFile && editorRef.current) {
      const currentContent = files[activeFile] || '';
      updateFile(activeFile, currentContent + code);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    vibrateTouch();
  };

  const handleFileSelect = (filename) => {
    setActiveFile(filename);
    setShowFileList(false);
    setShowUnifiedMenu(false); // Close the main menu
    vibrateTouch();
  };

  const openNewFileDialog = () => {
    setShowNewFileDialog(true);
    setShowUnifiedMenu(false); // Close the main menu
    setNewFileName('');
    setSelectedExtension('html');
    vibrateTouch();
  };

  const handleCreateNewFile = () => {
    if (!newFileName.trim()) return;
    
    const fullFileName = selectedExtension ? `${newFileName.trim()}.${selectedExtension}` : newFileName.trim();
    createFile(fullFileName);
    
    // Close dialogs and reset
    setShowNewFileDialog(false);
    setNewFileName('');
    setSelectedExtension('html');
    vibrateTouch();
  };

  const openFileBrowser = () => {
    setShowFileList(true);
    setShowUnifiedMenu(false); // Close the main menu
    vibrateTouch();
  };

  // Unified navigation handlers
  const togglePreview = () => {
    setActivePanel(activePanel === 'preview' ? null : 'preview');
    setShowUnifiedMenu(false);
    vibrateTouch();
  };

  const toggleAI = () => {
    setActivePanel(activePanel === 'ai' ? null : 'ai');
    setShowUnifiedMenu(false);
    vibrateTouch();
  };

  const handleSave = async () => {
    vibrateTouch();
    await saveCurrentProject();
  };

  const openAuth = () => {
    setShowAuth(true);
    setShowUnifiedMenu(false);
    vibrateTouch();
  };

  const openSettings = () => {
    setShowSettings(true);
    setShowUnifiedMenu(false);
    vibrateTouch();
  };

  const goHome = () => {
    navigate('/');
    vibrateTouch();
  };

  // NEW: Open preview in new window
  const openPreviewInNewWindow = () => {
    if (!activeFile || !files[activeFile]) return;
    
    const fileExtension = activeFile.split('.').pop()?.toLowerCase();
    let content = '';

    if (fileExtension === 'html') {
      const htmlContent = files[activeFile];
      const cssContent = files['style.css'] || files['styles.css'] || '';
      const jsContent = files['script.js'] || files['app.js'] || '';

      content = htmlContent;

      if (cssContent) {
        const cssTag = `<style>${cssContent}</style>`;
        if (content.includes('</head>')) {
          content = content.replace('</head>', `${cssTag}</head>`);
        } else {
          content = `<head>${cssTag}</head>${content}`;
        }
      }

      if (jsContent) {
        const jsTag = `<script>${jsContent}</script>`;
        if (content.includes('</body>')) {
          content = content.replace('</body>', `${jsTag}</body>`);
        } else {
          content = `${content}${jsTag}`;
        }
      }
    } else {
      content = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${activeFile}</title>
            <style>
              body { font-family: monospace; padding: 20px; background: #f5f5f5; }
              .container { background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
              pre { background: #f8f8f8; padding: 15px; border-radius: 4px; overflow-x: auto; }
            </style>
          </head>
          <body>
            <div class="container">
              <h2>${activeFile}</h2>
              <pre>${files[activeFile].replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
            </div>
          </body>
        </html>
      `;
    }

    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank', 'width=1200,height=800');
    
    if (newWindow) {
      newWindow.addEventListener('beforeunload', () => {
        URL.revokeObjectURL(url);
      });
    }
    
    vibrateTouch();
  };

  const renderPreviewContent = () => {
    if (!activeFile || !files[activeFile]) {
      return (
        <div className="flex items-center justify-center h-full bg-gray-950 text-gray-500">
          <div className="text-center p-8">
            <SafeIcon icon={FiEye} className="text-6xl mb-4 mx-auto text-gray-600" />
            <p className="text-lg mb-2">No File Selected</p>
            <p className="text-sm">Select an HTML file to preview</p>
          </div>
        </div>
      );
    }

    const fileExtension = activeFile.split('.').pop()?.toLowerCase();
    let content = '';

    if (fileExtension === 'html') {
      const htmlContent = files[activeFile];
      const cssContent = files['style.css'] || files['styles.css'] || '';
      const jsContent = files['script.js'] || files['app.js'] || '';

      content = htmlContent;

      if (cssContent) {
        const cssTag = `<style>${cssContent}</style>`;
        if (content.includes('</head>')) {
          content = content.replace('</head>', `${cssTag}</head>`);
        } else {
          content = `<head>${cssTag}</head>${content}`;
        }
      }

      if (jsContent) {
        const jsTag = `<script>${jsContent}</script>`;
        if (content.includes('</body>')) {
          content = content.replace('</body>', `${jsTag}</body>`);
        } else {
          content = `${content}${jsTag}`;
        }
      }
    } else {
      content = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              body { font-family: monospace; padding: 20px; margin: 0; background: #f5f5f5; }
              pre { background: white; padding: 15px; border-radius: 4px; overflow-x: auto; }
            </style>
          </head>
          <body>
            <h2>${activeFile}</h2>
            <pre>${files[activeFile].replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
          </body>
        </html>
      `;
    }

    return (
      <iframe
        className="w-full h-full border-0 bg-white"
        srcDoc={content}
        title="Mobile Preview"
        sandbox="allow-scripts allow-same-origin"
      />
    );
  };

  const renderMobileLayout = () => {
    // Updated to account for higher header (70px instead of 60px)
    const availableHeight = 'calc(100vh - 70px)';

    if (activePanel === 'preview') {
      return (
        <div className="flex flex-col h-full">
          {/* Editor Section - 40% */}
          <div className="flex-shrink-0 border-b border-gray-700" style={{ height: `calc(${availableHeight} * 0.4)` }}>
            <TouchOptimizedEditor
              ref={editorRef}
              value={activeFile ? files[activeFile] || '' : ''}
              onChange={handleEditorChange}
              language={activeFile ? getLanguage(activeFile) : 'plaintext'}
              fontSize={settings.fontSize}
              onFontSizeChange={(size) => updateSettings({ ...settings, fontSize: size })}
            />
          </div>
          {/* Preview Section - 60% */}
          <div className="flex-1" style={{ height: `calc(${availableHeight} * 0.6)` }}>
            {renderPreviewContent()}
          </div>
        </div>
      );
    }

    if (activePanel === 'ai') {
      return (
        <div className="flex flex-col h-full">
          {/* Editor Section - 40% */}
          <div className="flex-1 border-b border-gray-700" style={{ height: `calc(${availableHeight} * 0.4)` }}>
            <TouchOptimizedEditor
              ref={editorRef}
              value={activeFile ? files[activeFile] || '' : ''}
              onChange={handleEditorChange}
              language={activeFile ? getLanguage(activeFile) : 'plaintext'}
              fontSize={settings.fontSize}
              onFontSizeChange={(size) => updateSettings({ ...settings, fontSize: size })}
            />
          </div>
          {/* AI Assistant Section - 60% */}
          <div className="flex-1" style={{ height: `calc(${availableHeight} * 0.6)` }}>
            <AIAssistant />
          </div>
        </div>
      );
    }

    // Full editor view
    return (
      <div style={{ height: availableHeight }}>
        <TouchOptimizedEditor
          ref={editorRef}
          value={activeFile ? files[activeFile] || '' : ''}
          onChange={handleEditorChange}
          language={activeFile ? getLanguage(activeFile) : 'plaintext'}
          fontSize={settings.fontSize}
          onFontSizeChange={(size) => updateSettings({ ...settings, fontSize: size })}
        />
      </div>
    );
  };

  return (
    <div className={`h-screen flex flex-col bg-black ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* ENHANCED Header - Higher z-index and increased height with new buttons */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex-shrink-0 h-[70px] relative z-[9999]">
        <div className="flex items-center justify-between h-full">
          {/* Left side - Project Info */}
          <div className="flex items-center flex-1 min-w-0">
            <img
              src="https://res.cloudinary.com/frontly/image/upload/v1756676497/Angry_Branding_Logo_ykea75.png"
              alt="Angry Coder"
              className="w-7 h-7 mr-3"
            />
            <div className="flex-1 min-w-0">
              <h1 className="text-white font-medium text-base truncate">{currentProject.name}</h1>
              {activeFile && (
                <div className="text-xs text-gray-400 truncate">{activeFile}</div>
              )}
            </div>
          </div>

          {/* Center - Quick Action Buttons */}
          <div className="flex items-center gap-2 mx-3">
            {/* Publish Button */}
            <button
              onClick={() => setShowFilePublisher(true)}
              className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-all duration-200"
              title="🚀 Publish to Netlify"
            >
              <SafeIcon icon={FiGlobe} className="text-lg" />
            </button>

            {/* Open in New Window Button */}
            <button
              onClick={openPreviewInNewWindow}
              disabled={!activeFile || !files[activeFile]}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all duration-200"
              title="Open Preview in New Window"
            >
              <SafeIcon icon={FiExternalLink} className="text-lg" />
            </button>
          </div>

          {/* Right side - Unified Menu Button */}
          <button
            onClick={() => setShowUnifiedMenu(!showUnifiedMenu)}
            className="p-3 text-gray-400 hover:text-white rounded-xl relative bg-gray-800/50 hover:bg-gray-700/50 transition-all duration-200 shadow-lg"
          >
            <SafeIcon icon={showUnifiedMenu ? FiX : FiMenu} className="text-xl" />
            {syncing && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-400 rounded-full animate-pulse"></div>
            )}
          </button>
        </div>
      </div>

      {/* Main Editor Area - Updated height calculation */}
      <div className="flex-1 relative">
        {renderMobileLayout()}
      </div>

      {/* Unified Menu Overlay - HIGHEST z-index with SCROLLABLE content */}
      <AnimatePresence>
        {showUnifiedMenu && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-[10000]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowUnifiedMenu(false)}
            />
            <motion.div
              className="fixed top-20 right-4 bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl z-[10001] min-w-[280px] max-w-[320px] max-h-[calc(100vh-100px)] overflow-hidden flex flex-col"
              initial={{ opacity: 0, scale: 0.8, x: 20, y: -20 }}
              animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, x: 20, y: -20 }}
              transition={{ type: 'spring', damping: 20 }}
            >
              {/* Menu Header - Fixed */}
              <div className="p-4 border-b border-gray-700 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-semibold">Angry Coder</h3>
                  <div className="flex items-center gap-2">
                    {syncing ? (
                      <div className="flex items-center text-orange-400 text-xs">
                        <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse mr-1"></div>
                        Syncing...
                      </div>
                    ) : (
                      <div className="flex items-center text-green-400 text-xs">
                        <div className="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                        Saved
                      </div>
                    )}
                  </div>
                </div>
                {activeFile && (
                  <div className="text-xs text-gray-400 mt-1">
                    Current: {activeFile}
                  </div>
                )}
              </div>

              {/* Menu Items - SCROLLABLE */}
              <div className="flex-1 overflow-y-auto p-2">
                {/* Navigation Section */}
                <div className="mb-3">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 px-2">Navigation</div>
                  <button
                    onClick={goHome}
                    className="w-full flex items-center px-3 py-3 text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiHome} className="mr-3 text-lg" />
                    <span>Home</span>
                  </button>
                </div>

                {/* View Section */}
                <div className="mb-3">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 px-2">View</div>
                  <button
                    onClick={() => {
                      setActivePanel(null);
                      setShowUnifiedMenu(false);
                    }}
                    className={`w-full flex items-center px-3 py-3 rounded-lg transition-colors ${
                      !activePanel
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <SafeIcon icon={FiFile} className="mr-3 text-lg" />
                    <span>Editor Only</span>
                  </button>
                  <button
                    onClick={togglePreview}
                    className={`w-full flex items-center px-3 py-3 rounded-lg transition-colors ${
                      activePanel === 'preview'
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <SafeIcon icon={FiEye} className="mr-3 text-lg" />
                    <span>Split Preview</span>
                  </button>
                  <button
                    onClick={toggleAI}
                    className={`w-full flex items-center px-3 py-3 rounded-lg transition-colors ${
                      activePanel === 'ai'
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <SafeIcon icon={BiBot} className="mr-3 text-lg" />
                    <span>AI Assistant</span>
                  </button>
                </div>

                {/* Files Section */}
                <div className="mb-3">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 px-2">Files</div>
                  <button
                    onClick={openFileBrowser}
                    className="w-full flex items-center px-3 py-3 text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiFolder} className="mr-3 text-lg" />
                    <span>Browse Files ({Object.keys(files).length})</span>
                  </button>
                  <button
                    onClick={openNewFileDialog}
                    className="w-full flex items-center px-3 py-3 text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiPlus} className="mr-3 text-lg" />
                    <span>New File</span>
                  </button>
                </div>

                {/* Tools Section */}
                <div className="mb-3">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 px-2">Tools</div>
                  <button
                    onClick={() => {
                      handleSave();
                      setShowUnifiedMenu(false);
                    }}
                    disabled={syncing}
                    className={`w-full flex items-center px-3 py-3 text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors disabled:opacity-75 ${syncing ? 'animate-pulse' : ''}`}
                  >
                    <SafeIcon icon={syncing ? FiWifi : FiSave} className="mr-3 text-lg" />
                    <span>Save Project</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowVoiceInput(!showVoiceInput);
                      setShowUnifiedMenu(false);
                    }}
                    className="w-full flex items-center px-3 py-3 text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiMic} className="mr-3 text-lg" />
                    <span>Voice Coding</span>
                  </button>
                  <button
                    onClick={() => {
                      toggleFullscreen();
                      setShowUnifiedMenu(false);
                    }}
                    className="w-full flex items-center px-3 py-3 text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors"
                  >
                    <SafeIcon icon={isFullscreen ? FiMinimize : FiMaximize} className="mr-3 text-lg" />
                    <span>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
                  </button>
                </div>

                {/* Account Section */}
                <div className="mb-3">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 px-2">Account</div>
                  {user ? (
                    <div className="bg-gray-700/50 rounded-lg p-3 mb-2">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center mr-3">
                          <SafeIcon icon={FiUser} className="text-white text-sm" />
                        </div>
                        <div className="flex-1">
                          <div className="text-white text-sm font-medium truncate">
                            {user.email?.split('@')[0]}
                          </div>
                          <div className="text-green-400 text-xs">Signed In</div>
                        </div>
                      </div>
                      <button
                        onClick={openSettings}
                        className="w-full flex items-center px-2 py-2 text-gray-300 hover:bg-gray-600 hover:text-white rounded text-sm transition-colors"
                      >
                        <SafeIcon icon={FiSettings} className="mr-2" />
                        <span>Settings</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={openAuth}
                      className="w-full flex items-center px-3 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiLogIn} className="mr-3 text-lg" />
                      <span>Sign In</span>
                    </button>
                  )}
                </div>

                {/* Settings for signed-in users */}
                {user && (
                  <div className="mb-3">
                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 px-2">Settings</div>
                    <button
                      onClick={openSettings}
                      className="w-full flex items-center px-3 py-3 text-gray-300 hover:bg-gray-700 hover:text-white rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiSettings} className="mr-3 text-lg" />
                      <span>Preferences</span>
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Voice Input Overlay - FIXED positioning */}
      <AnimatePresence>
        {showVoiceInput && (
          <motion.div
            className="fixed bottom-4 left-4 right-4 z-[9998] flex flex-col items-start"
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
          >
            <VoiceCoding
              onInsertCode={handleVoiceInsert}
              onCommand={handleVoiceCommand}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* FIXED: File List Modal - Now perfectly centered */}
      <AnimatePresence>
        {showFileList && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-[10010]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFileList(false)}
            />
            <motion.div
              className="fixed inset-4 z-[10011] flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-white font-medium">Project Files</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={openNewFileDialog}
                      className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
                    >
                      <SafeIcon icon={FiPlus} className="text-white" />
                    </button>
                    <button
                      onClick={() => setShowFileList(false)}
                      className="p-2 text-gray-400 hover:text-white"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {Object.keys(files).map((filename) => (
                    <button
                      key={filename}
                      onClick={() => handleFileSelect(filename)}
                      className={`w-full flex items-center px-4 py-3 text-left hover:bg-gray-700 transition-colors ${
                        activeFile === filename ? 'bg-gray-700 text-white' : 'text-gray-300'
                      }`}
                    >
                      <SafeIcon icon={FiFile} className="mr-3 text-blue-400" />
                      <span className="flex-1 truncate">{filename}</span>
                      {activeFile === filename && (
                        <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                      )}
                    </button>
                  ))}
                  {Object.keys(files).length === 0 && (
                    <div className="p-4 text-center text-gray-500">No files yet</div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* FIXED: New File Dialog - Now perfectly centered */}
      <AnimatePresence>
        {showNewFileDialog && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-[10020]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowNewFileDialog(false)}
            />
            <motion.div
              className="fixed inset-4 z-[10021] flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-white font-medium flex items-center">
                    <SafeIcon icon={FiCode} className="mr-2 text-blue-400" />
                    Create New File
                  </h3>
                  <button
                    onClick={() => setShowNewFileDialog(false)}
                    className="p-2 text-gray-400 hover:text-white"
                  >
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
                
                <div className="p-4 space-y-4">
                  {/* File Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">File Type</label>
                    <select
                      value={selectedExtension}
                      onChange={(e) => setSelectedExtension(e.target.value)}
                      className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none text-white text-sm"
                    >
                      {fileExtensions.map((fileType) => (
                        <option key={fileType.ext} value={fileType.ext}>
                          {fileType.name} (.{fileType.ext})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* File Name Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      File Name {selectedExtension && `(.${selectedExtension})`}
                    </label>
                    <input
                      type="text"
                      value={newFileName}
                      onChange={(e) => setNewFileName(e.target.value)}
                      placeholder={selectedExtension ? "filename" : "filename.ext"}
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none text-white"
                      onKeyPress={(e) => e.key === 'Enter' && handleCreateNewFile()}
                      autoFocus
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleCreateNewFile}
                      disabled={!newFileName.trim()}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 px-4 py-3 rounded-lg font-medium transition-colors duration-200"
                    >
                      Create File
                    </button>
                    <button
                      onClick={() => setShowNewFileDialog(false)}
                      className="px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors duration-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* File Publisher Modal */}
      {showFilePublisher && (
        <FilePublisher onClose={() => setShowFilePublisher(false)} />
      )}

      {/* Modals - Lower z-index */}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode="signin" />
    </div>
  );
};

export default MobileCodeEditor;